import { citationTarget } from '@/citations/reference';
import { isWorkspaceLink } from '@/lib/workspace-links';
import { parseWorkspaceRoute } from './navigation';
import type { CanvasTarget } from './types';

export type ActivitySourceReference =
  | { kind: 'internal'; target: CanvasTarget; href: string }
  | { kind: 'external'; href: string; label: string }
  | { kind: 'id'; id: string }
  | { kind: 'plain'; text: string };

/** Keep signed workspace citations on their actual object; never treat a URL as an ID. */
export function activitySourceReference(
  sourceId: string,
  href?: string,
  base?: string,
): ActivitySourceReference {
  const candidate = href ?? sourceId;
  const current = base ?? (typeof window !== 'undefined' ? window.location.href : undefined);
  if (!href && /^[A-Za-z0-9_-]{1,128}$/.test(candidate)) return { kind: 'id', id: sourceId };
  const citation = citationTarget(candidate, current);
  if (citation) {
    const target = parseWorkspaceRoute(new URL(candidate, current).hash).canvas;
    if (target) return { kind: 'internal', target, href: citation.href };
  }
  // A malformed internal citation, or one naming a retired company fact, must not
  // fall through to ordinary link navigation.
  if (isWorkspaceLink(candidate, current)) return { kind: 'plain', text: sourceId };
  try {
    const url = new URL(candidate);
    if ((url.protocol === 'https:' || url.protocol === 'http:') && !url.username && !url.password) {
      return {
        kind: 'external',
        href: url.href,
        label: url.hostname + (url.pathname === '/' ? '' : url.pathname),
      };
    }
  } catch {
    // Malformed references remain non-executable evidence.
  }
  return { kind: 'plain', text: sourceId };
}
